import {
  parseAgendaViewableProfessionalIds,
  professionalAgendaViewOthersEnabled,
  resolveAllowedAppointmentProfessionalIds,
  userAgendaFollowsProfessionalScope,
} from './professionalAgendaAccess';

/** Quais profissionais aparecem marcados ao abrir a agenda. */
export type AgendaProfessionalsDefault = 'mine' | 'all' | 'last';
export type AgendaDefaultView = 'last' | 'day' | 'week' | 'month';
export type AgendaDefaultDayLayout = 'last' | 'professionals' | 'unified' | 'list';

export interface AgendaUserPreferences {
  professionals_default?: AgendaProfessionalsDefault;
  default_view?: AgendaDefaultView;
  day_layout?: AgendaDefaultDayLayout;
}

interface AgendaPrefsUserLike {
  role?: string | null;
  professional_id?: string | null;
  professional?: any;
  preferences?: Record<string, any> | null;
}

const PROFESSIONALS_DEFAULTS: AgendaProfessionalsDefault[] = ['mine', 'all', 'last'];
const DEFAULT_VIEWS: AgendaDefaultView[] = ['last', 'day', 'week', 'month'];
const DAY_LAYOUTS: AgendaDefaultDayLayout[] = ['last', 'professionals', 'unified', 'list'];

export function readAgendaUserPreferences(user: AgendaPrefsUserLike | null | undefined): AgendaUserPreferences {
  const raw = user?.preferences?.agenda;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: AgendaUserPreferences = {};
  if (PROFESSIONALS_DEFAULTS.includes(raw.professionals_default)) out.professionals_default = raw.professionals_default;
  if (DEFAULT_VIEWS.includes(raw.default_view)) out.default_view = raw.default_view;
  if (DAY_LAYOUTS.includes(raw.day_layout)) out.day_layout = raw.day_layout;
  return out;
}

/** Profissionais que o usuário vinculado pode ver além do próprio (perfil professional respeita a liberação do cadastro). */
export function agendaAllowedProfessionalIdsForUser(user: AgendaPrefsUserLike | null | undefined): string[] {
  const own = user?.professional_id || '';
  if (!own || !userAgendaFollowsProfessionalScope(user)) return own ? [own] : [];
  const prof = user?.professional;
  const meta = prof?.metadata ?? null;
  const enabled =
    prof?.agenda_view_other_professionals_enabled === true
    || professionalAgendaViewOthersEnabled(meta);
  const viewableIds = prof?.agenda_viewable_professional_ids;
  const ids = Array.isArray(viewableIds) ? viewableIds : parseAgendaViewableProfessionalIds(meta);
  return resolveAllowedAppointmentProfessionalIds(own, {
    agenda_view_other_professionals_enabled: enabled,
    agenda_viewable_professional_ids: ids,
  });
}

/** Usuário vê a agenda de outros profissionais (pré-requisito para a escolha “só a minha / todos”). */
export function userCanViewOtherProfessionalsAgenda(user: AgendaPrefsUserLike | null | undefined): boolean {
  if (!user) return false;
  if (!userAgendaFollowsProfessionalScope(user)) return true;
  return agendaAllowedProfessionalIdsForUser(user).length > 1;
}

/** Padrão quando o usuário ainda não escolheu: recepção vê todos; quem tem profissional vinculado vê só a própria. */
export function defaultAgendaProfessionalsMode(user: AgendaPrefsUserLike | null | undefined): AgendaProfessionalsDefault {
  if (user?.role === 'receptionist') return 'all';
  if (user?.professional_id) return 'mine';
  return 'all';
}

export function resolveAgendaProfessionalsMode(user: AgendaPrefsUserLike | null | undefined): AgendaProfessionalsDefault {
  const chosen = readAgendaUserPreferences(user).professionals_default;
  const mode = chosen ?? defaultAgendaProfessionalsMode(user);
  if (mode === 'mine' && !user?.professional_id) return 'all';
  return mode;
}

export type AgendaDayLayoutMode = 'professionals' | 'unified' | 'list';

/** Layout da vista dia: padrão do produto é uma coluna por profissional; «último usado» só se escolhido no perfil. */
export function resolveAgendaDayLayout(
  user: AgendaPrefsUserLike | null | undefined,
  readLastUsed?: () => AgendaDayLayoutMode,
): AgendaDayLayoutMode {
  const chosen = readAgendaUserPreferences(user).day_layout;
  if (!chosen) return 'professionals';
  if (chosen === 'last') {
    const stored = readLastUsed?.();
    if (stored === 'unified' || stored === 'list' || stored === 'professionals') return stored;
    return 'professionals';
  }
  return chosen;
}
